import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import { cores } from '../../theme';

import {
    Typography, Container, Box, CircularProgress, Alert, Paper, Button, List, ListItem, ListItemText, Divider,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField, Autocomplete, LinearProgress,
    IconButton, Avatar, ListItemAvatar, Fade, ListItemSecondaryAction, Chip, useTheme
} from '@mui/material';
import {
    ArrowBack,
    Add as AddIcon,
    Delete as DeleteIcon,
    Assignment as AssignmentIcon,
    Close as CloseIcon,
    Grading as GradingIcon,
    LibraryAdd as LibraryAddIcon
} from '@mui/icons-material';

const CasoDetalheAdminPagina = () => {
    const { id: casoId } = useParams();
    const [caso, setCaso] = useState(null);
    const [criteriosDoCaso, setCriteriosDoCaso] = useState([]);
    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState('');
    const [modalAberto, setModalAberto] = useState(false);
    const [opcoesBusca, setOpcoesBusca] = useState([]);
    const [criterioSelecionado, setCriterioSelecionado] = useState(null);
    const [peso, setPeso] = useState(25);
    const [confirmarExclusao, setConfirmarExclusao] = useState({ aberto: false, criterioId: null });
    const navegar = useNavigate();
    const dialogRef = useRef(null);
    const theme = useTheme();

    const pesoTotal = useMemo(() => {
        return criteriosDoCaso.reduce((total, criterio) => total + Number(criterio.peso), 0);
    }, [criteriosDoCaso]);

    const buscarDados = useCallback(async () => {
        try {
            const [respCaso, respCriterios] = await Promise.all([
                api.get(`/api/casos-clinicos/${casoId}`),
                api.get(`/api/casos-clinicos/${casoId}/criterios`),
            ]);
            setCaso(respCaso.data);
            setCriteriosDoCaso(respCriterios.data);
        } catch (err) {
            setErro('Falha ao carregar dados.');
        } finally {
            if (carregando) setCarregando(false);
        }
    }, [casoId, carregando]);

    useEffect(() => {
        buscarDados();
    }, [buscarDados]);

    const abrirModal = async () => {
        setErro('');
        try {
            const resposta = await api.get('/api/criterios');
            const criteriosAtuaisIds = new Set(criteriosDoCaso.map((c) => c.id));
            const criteriosDisponiveis = resposta.data.filter(
                (c) => !criteriosAtuaisIds.has(c.id)
            );
            setOpcoesBusca(criteriosDisponiveis);
            setModalAberto(true);
        } catch (err) {
            setErro('Não foi possível carregar a biblioteca de critérios.');
        }
    };

    const fecharModal = () => {
        setModalAberto(false);
        setCriterioSelecionado(null);
        setPeso(25);
    };

    const lidarComAdicionarCriterio = async () => {
        const pesoNumerico = parseInt(peso, 10);
        if (!criterioSelecionado || !pesoNumerico) {
            setErro('Por favor, selecione um critério e defina um peso válido.');
            return;
        }

        if (pesoTotal + pesoNumerico > 100) {
            setErro(`O peso total não pode exceder 100. Você ainda tem ${100 - pesoTotal} pontos para distribuir.`);
            return;
        }

        try {
            const payload = {
                criterio_id: criterioSelecionado.id,
                ordem: (criteriosDoCaso.length + 1) * 10,
                peso: pesoNumerico,
            };
            await api.post(`/api/casos-clinicos/${casoId}/criterios`, payload);
            fecharModal();
            buscarDados();
        } catch (err) {
            setErro('Erro ao adicionar critério.');
        }
    };

    const abrirConfirmacaoExclusao = (criterioId) => {
        setConfirmarExclusao({ aberto: true, criterioId });
    };

    const fecharConfirmacaoExclusao = () => {
        setConfirmarExclusao({ aberto: false, criterioId: null });
    };

    const lidarComRemoverCriterio = async () => {
        try {
            await api.delete(`/api/casos-clinicos/${casoId}/criterios/${confirmarExclusao.criterioId}`);
            fecharConfirmacaoExclusao();
            buscarDados();
        } catch (err) {
            setErro('Erro ao remover critério.');
            fecharConfirmacaoExclusao();
        }
    };

    if (carregando) {
        return (
            <Container maxWidth="lg" sx={{ py: 5, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress sx={{ color: cores.primaria.principal }} />
            </Container>
        );
    }

    if (erro && !caso) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Alert
                    severity="error"
                    sx={{
                        borderRadius: 2,
                        backgroundColor: cores.erro.claro,
                        borderColor: cores.erro.medio,
                        color: cores.erro.escuro,
                        '& .MuiAlert-icon': {
                            alignItems: 'center'
                        }
                    }}
                >
                    {erro}
                </Alert>
            </Container>
        );
    }

    if (!caso) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Alert
                    severity="info"
                    sx={{
                        borderRadius: 2,
                        '& .MuiAlert-icon': {
                            alignItems: 'center'
                        }
                    }}
                >
                    Caso clínico não encontrado.
                </Alert>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ py: { xs: 3, sm: 4 } }}>
            <Fade in={true} timeout={500}>
                <Paper sx={{ p: { xs: 2, sm: 4 }, borderRadius: 3, boxShadow: cores.sombras.medio }}>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mb: 4,
                        p: { xs: 2, sm: 3 },
                        borderRadius: 2,
                        background: cores.gradientes.primario,
                        color: 'white'
                    }}>
                        <IconButton
                            onClick={() => navegar('/gerenciar-casos')}
                            sx={{
                                mr: 2,
                                color: 'white',
                                bgcolor: 'rgba(255,255,255,0.2)',
                                '&:hover': {
                                    bgcolor: 'rgba(255,255,255,0.3)',
                                }
                            }}
                        >
                            <ArrowBack />
                        </IconButton>
                        <Box sx={{ flex: 1 }}>
                            <Typography
                                variant="h4"
                                component="h1"
                                sx={{
                                    fontWeight: 700,
                                    fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                                    lineHeight: 1.2
                                }}
                            >
                                Rubrica de Avaliação
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{
                                    opacity: 0.9,
                                    fontSize: { xs: '0.9rem', sm: '1rem' }
                                }}
                            >
                                {caso.titulo}
                            </Typography>
                        </Box>
                    </Box>

                    <Fade in={true} timeout={600}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: { xs: 2, sm: 3 },
                                mb: 3,
                                bgcolor: cores.fundo.claro,
                                borderRadius: 2,
                                border: `1px solid ${cores.cinza.claro}`,
                                boxShadow: cores.sombras.suave
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                                <Avatar
                                    sx={{
                                        mr: 2,
                                        bgcolor: `rgba(${parseInt(cores.primaria.principal.slice(1, 3), 16)}, ${parseInt(cores.primaria.principal.slice(3, 5), 16)}, ${parseInt(cores.primaria.principal.slice(5, 7), 16)}, 0.1)`,
                                        color: cores.primaria.principal
                                    }}
                                >
                                    <AssignmentIcon />
                                </Avatar>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="overline" sx={{ color: cores.cinza.medio, fontWeight: 600 }}>
                                        DISTRIBUIÇÃO DE PONTOS
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: cores.cinza.escuro, mb: 2 }}>
                                        Adicione critérios e distribua 100 pontos entre eles
                                    </Typography>

                                    <Box sx={{ mt: 1 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 600, color: cores.primaria.principal }}>
                                                {pesoTotal === 100 ? (
                                                    <Chip
                                                        size="small"
                                                        label="Rubrica Completa"
                                                        color="success"
                                                        sx={{ fontWeight: 600 }}
                                                    />
                                                ) : (
                                                    `${pesoTotal}/100 pontos distribuídos`
                                                )}
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: pesoTotal === 100 ? cores.sucesso.principal : cores.aviso.principal }}>
                                                {pesoTotal === 100 ? 'Pronto para uso' : `Faltam ${100 - pesoTotal} pontos`}
                                            </Typography>
                                        </Box>
                                        <LinearProgress
                                            variant="determinate"
                                            value={pesoTotal}
                                            sx={{
                                                height: 8,
                                                borderRadius: 4,
                                                bgcolor: 'rgba(203, 213, 225, 0.4)',
                                                '& .MuiLinearProgress-bar': {
                                                    bgcolor: pesoTotal === 100 ? cores.sucesso.principal : cores.aviso.principal
                                                }
                                            }}
                                        />
                                    </Box>
                                </Box>
                            </Box>
                        </Paper>
                    </Fade>

                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 2,
                            mt: 4
                        }}
                    >
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 700,
                                color: cores.primaria.principal,
                                fontSize: { xs: '1.1rem', sm: '1.25rem' }
                            }}
                        >
                            Critérios de Avaliação
                        </Typography>
                        <Button
                            variant="contained"
                            onClick={abrirModal}
                            disabled={pesoTotal >= 100}
                            startIcon={<AddIcon />}
                            sx={{
                                borderRadius: 2,
                                bgcolor: cores.primaria.principal,
                                fontWeight: 600,
                                boxShadow: cores.sombras.suave,
                                '&:hover': {
                                    bgcolor: cores.primaria.escura,
                                },
                                '&.Mui-disabled': {
                                    bgcolor: cores.cinza.claro,
                                    color: cores.cinza.medio
                                }
                            }}
                        >
                            Adicionar Critério
                        </Button>
                    </Box>

                    {erro && (
                        <Alert
                            severity="error"
                            sx={{
                                mt: 2,
                                mb: 3,
                                borderRadius: 2,
                                backgroundColor: cores.erro.claro,
                                borderColor: cores.erro.medio,
                                color: cores.erro.escuro,
                                '& .MuiAlert-icon': {
                                    alignItems: 'center'
                                }
                            }}
                        >
                            {erro}
                        </Alert>
                    )}

                    <Fade in={true} timeout={700}>
                        <Paper
                            elevation={0}
                            sx={{
                                border: `1px solid ${cores.cinza.claro}`,
                                borderRadius: 2,
                                overflow: 'hidden',
                                boxShadow: cores.sombras.suave
                            }}
                        >
                            {criteriosDoCaso.length > 0 ? (
                                <List sx={{ p: 0 }}>
                                    {criteriosDoCaso.map((criterio, i) => (
                                        <Fade key={criterio.id} in={true} timeout={500 + (i * 100)}>
                                            <Box component="div">
                                                <ListItem
                                                    sx={{
                                                        py: 2,
                                                        px: 3,
                                                        transition: 'background-color 0.2s',
                                                        '&:hover': {
                                                            bgcolor: cores.fundo.claro
                                                        }
                                                    }}
                                                >
                                                    <ListItemAvatar>
                                                        <Avatar
                                                            sx={{
                                                                bgcolor: `rgba(${parseInt(cores.primaria.principal.slice(1, 3), 16)}, ${parseInt(cores.primaria.principal.slice(3, 5), 16)}, ${parseInt(cores.primaria.principal.slice(5, 7), 16)}, 0.1)`,
                                                                color: cores.primaria.principal
                                                            }}
                                                        >
                                                            <GradingIcon />
                                                        </Avatar>
                                                    </ListItemAvatar>
                                                    <ListItemText
                                                        primary={
                                                            <Typography variant="body1" sx={{ fontWeight: 600, color: cores.cinza.escuro }}>
                                                                {criterio.descricao}
                                                            </Typography>
                                                        }
                                                        secondary={
                                                            <Chip
                                                                size="small"
                                                                label={`Peso: ${criterio.peso}`}
                                                                sx={{
                                                                    mt: 1,
                                                                    fontWeight: 600,
                                                                    bgcolor: `rgba(${parseInt(cores.primaria.principal.slice(1, 3), 16)}, ${parseInt(cores.primaria.principal.slice(3, 5), 16)}, ${parseInt(cores.primaria.principal.slice(5, 7), 16)}, 0.08)`,
                                                                    color: cores.primaria.principal,
                                                                }}
                                                            />
                                                        }
                                                    />
                                                    <ListItemSecondaryAction>
                                                        <IconButton
                                                            edge="end"
                                                            onClick={() => abrirConfirmacaoExclusao(criterio.id)}
                                                            sx={{
                                                                color: cores.erro.principal,
                                                                '&:hover': {
                                                                    bgcolor: `rgba(${parseInt(cores.erro.principal.slice(1, 3), 16)}, ${parseInt(cores.erro.principal.slice(3, 5), 16)}, ${parseInt(cores.erro.principal.slice(5, 7), 16)}, 0.08)`
                                                                }
                                                            }}
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </ListItemSecondaryAction>
                                                </ListItem>
                                                {i < criteriosDoCaso.length - 1 && <Divider />}
                                            </Box>
                                        </Fade>
                                    ))}
                                </List>
                            ) : (
                                <Box
                                    sx={{
                                        p: 4,
                                        textAlign: 'center',
                                        color: cores.cinza.medio,
                                        borderRadius: 2,
                                        border: `1px dashed ${cores.cinza.claro}`
                                    }}
                                >
                                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                                        Nenhum critério adicionado
                                    </Typography>
                                    <Typography variant="body1">
                                        Clique em "Adicionar Critério" para começar a montar sua rubrica de avaliação.
                                    </Typography>
                                </Box>
                            )}
                        </Paper>
                    </Fade>
                </Paper>
            </Fade>

            <Dialog
                open={modalAberto}
                onClose={fecharModal}
                fullWidth
                maxWidth="sm"
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        boxShadow: cores.sombras.forte
                    }
                }}
                ref={dialogRef}
            >
                <DialogTitle sx={{
                    pb: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LibraryAddIcon sx={{ color: cores.primaria.principal }} />
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            Adicionar Critério da Biblioteca
                        </Typography>
                    </Box>
                    <IconButton onClick={fecharModal} size="small" sx={{ color: cores.cinza.medio }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ pt: 1 }}>
                    <Typography variant="body2" sx={{ mb: 2, color: cores.cinza.medio }}>
                        Selecione um critério da biblioteca e defina o peso para este caso.
                    </Typography>

                    <Autocomplete
                        options={opcoesBusca}
                        getOptionLabel={(option) => option.descricao}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        onChange={(e, v) => setCriterioSelecionado(v)}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Buscar critério na biblioteca"
                                margin="normal"
                                fullWidth
                                variant="outlined"
                                InputProps={{
                                    ...params.InputProps,
                                    sx: { borderRadius: 2 }
                                }}
                            />
                        )}
                        noOptionsText="Nenhum critério disponível"
                        disablePortal
                    />

                    <TextField
                        label="Peso do Critério na Avaliação"
                        type="number"
                        fullWidth
                        margin="normal"
                        value={peso}
                        onChange={(e) => {
                            const valor = parseInt(e.target.value, 10);
                            if (valor > 0) setPeso(valor);
                        }}
                        helperText={`Você tem ${100 - pesoTotal} pontos restantes para distribuir.`}
                        InputProps={{
                            sx: { borderRadius: 2 },
                            inputProps: { min: 1, max: 100 - pesoTotal }
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button
                        onClick={fecharModal}
                        variant="outlined"
                        sx={{
                            borderRadius: 2,
                            borderColor: cores.cinza.claro,
                            color: cores.cinza.escuro,
                            '&:hover': {
                                borderColor: cores.cinza.medio,
                                bgcolor: 'rgba(203, 213, 225, 0.1)'
                            }
                        }}
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={lidarComAdicionarCriterio}
                        variant="contained"
                        disabled={!criterioSelecionado}
                        sx={{
                            borderRadius: 2,
                            bgcolor: cores.primaria.principal,
                            fontWeight: 600,
                            ml: 2,
                            '&:hover': {
                                bgcolor: cores.primaria.escura,
                            },
                            '&.Mui-disabled': {
                                bgcolor: cores.cinza.claro,
                                color: cores.cinza.medio
                            }
                        }}
                    >
                        Adicionar Critério
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={confirmarExclusao.aberto}
                onClose={fecharConfirmacaoExclusao}
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        boxShadow: cores.sombras.forte,
                        maxWidth: 450
                    }
                }}
            >
                <DialogTitle sx={{
                    color: cores.erro.escuro,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontWeight: 700
                }}>
                    <DeleteIcon color="error" />
                    Remover Critério
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ mb: 2, color: cores.cinza.escuro }}>
                        Tem certeza que deseja remover este critério da rubrica de avaliação?
                    </Typography>
                    <Typography variant="body2" sx={{ color: cores.cinza.medio }}>
                        Esta ação não pode ser desfeita e o peso deste critério precisará ser redistribuído.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button
                        onClick={fecharConfirmacaoExclusao}
                        variant="outlined"
                        sx={{
                            borderRadius: 2,
                            borderColor: cores.cinza.claro,
                            color: cores.cinza.escuro,
                            '&:hover': {
                                borderColor: cores.cinza.medio,
                                bgcolor: 'rgba(203, 213, 225, 0.1)'
                            }
                        }}
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={lidarComRemoverCriterio}
                        variant="contained"
                        color="error"
                        sx={{
                            borderRadius: 2,
                            fontWeight: 600,
                            boxShadow: `0 4px 12px rgba(${parseInt(cores.erro.principal.slice(1, 3), 16)}, ${parseInt(cores.erro.principal.slice(3, 5), 16)}, ${parseInt(cores.erro.principal.slice(5, 7), 16)}, 0.2)`,
                            '&:hover': {
                                bgcolor: cores.erro.escuro,
                            }
                        }}
                    >
                        Remover
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default CasoDetalheAdminPagina;